import { api } from './client';

export const sessionsApi = {
  // Story session endpoints

  /**
   * List all sessions for a story
   * @param {string} storyId - Story UUID
   * @returns {Promise<Array>} List of sessions
   */
  list: (storyId, { room, scene } = {}) => {
    const params = new URLSearchParams();
    if (room) params.set('room', room);
    if (scene) params.set('scene_id', scene);
    const query = params.toString();
    return api.get(`/stories/${storyId}/sessions${query ? `?${query}` : ''}`);
  },

  /**
   * The thread for one book room. shared=true means the column is not applied yet.
   * sceneId narrows Write to one scene. Other rooms ignore it.
   */
  roomChat: (storyId, room, prefer, sceneId) => {
    const params = new URLSearchParams({ room });
    if (prefer) params.set('prefer', prefer);
    if (sceneId) params.set('scene_id', sceneId);
    return api.get(`/stories/${storyId}/room-chat?${params.toString()}`);
  },

  /**
   * Create a new session for a story
   * @param {string} storyId - Story UUID
   * @param {Object} data - Session creation data
   * @param {string} [data.name] - Session name (auto-generated if not provided)
   * @param {string} [data.description] - Session description
   * @param {string} [data.guidance_mode='explore'] - 'explore' or 'focus'
   * @param {string} [data.template='open'] - Template type (character, plot, scene, world, logline, dialogue, outline_section, open, problem)
   * @param {string|null} [data.focus_target_name] - Name/description of element being focused on (for focus mode)
   * @returns {Promise<Object>} Created session
   */
  create: (storyId, data) => api.post(`/stories/${storyId}/sessions`, data),

  /**
   * Get a session by ID
   * @param {string} id - Session UUID
   * @returns {Promise<Object>} Session data including guidance_mode, template, focus_target_name
   */
  get: (id, options) => api.get(`/sessions/${id}`, options),

  /**
   * Update a session
   * @param {string} id - Session UUID
   * @param {Object} data - Update data (may include guidance_mode, template, focus_target_name)
   * @returns {Promise<Object>} Updated session
   */
  update: (id, data) => api.patch(`/sessions/${id}`, data),

  /**
   * Delete a session
   * @param {string} id - Session UUID
   * @returns {Promise<void>}
   */
  delete: (id) => api.delete(`/sessions/${id}`),

  // Project Sessions: Project-scoped session endpoints

  /**
   * List all sessions for a project (series planning sessions)
   * @param {string} projectId - Project UUID
   * @returns {Promise<Array>} List of sessions
   */
  listProject: (projectId) => api.get(`/projects/${projectId}/sessions`),

  /**
   * Create a new session for a project (series planning)
   * @param {string} projectId - Project UUID
   * @param {Object} data - Session creation data
   * @param {string} [data.name] - Session name
   * @returns {Promise<Object>} Created session
   */
  createProject: (projectId, data) => api.post(`/projects/${projectId}/sessions`, data),

  // Session Summary Service endpoints
  /**
   * Get session status (message count, limit, warning level, etc.)
   * @param {string} sessionId
   * @returns {Promise} SessionStatusResponse
   */
  getStatus: (sessionId) => api.get(`/sessions/${sessionId}/status`),

  /**
   * Create a manual interim summary
   * @param {string} sessionId
   * @param {Object} [options] - Optional settings
   * @param {string} [options.instructions] - Optional instructions for summary generation
   * @returns {Promise} InterimSummaryResponse
   */
  createInterimSummary: (sessionId, options = {}) => {
    const body = options.instructions ? { instructions: options.instructions } : undefined;
    return api.post(`/sessions/${sessionId}/summary/interim`, body);
  },

  /**
   * Update an existing interim summary
   * @param {string} sessionId - Session UUID
   * @param {string} summaryId - Summary UUID
   * @param {string} content - New content for the summary
   * @returns {Promise} InterimSummaryDetailResponse
   */
  updateInterimSummary: (sessionId, summaryId, content) =>
    api.patch(`/sessions/${sessionId}/summary/${summaryId}`, { content }),

  /**
   * Create a manual checkpoint (DEPRECATED - use createInterimSummary)
   * @param {string} sessionId
   * @returns {Promise} InterimSummaryResponse
   * @deprecated Use createInterimSummary instead
   */
  createCheckpoint: (sessionId) => api.post(`/sessions/${sessionId}/summary/interim`),

  /**
   * End a session and generate summary
   * @param {string} sessionId
   * @param {boolean} atLimit - If true, creates continuation session
   * @returns {Promise} EndSessionResponse
   */
  end: (sessionId, atLimit = false) =>
    api.post(`/sessions/${sessionId}/end?at_limit=${atLimit}`),

  /**
   * Get session summary
   * @param {string} sessionId
   * @returns {Promise} SessionSummaryResponse
   */
  getSummary: (sessionId) => api.get(`/sessions/${sessionId}/summary`),

  /**
   * Get latest interim summary
   * @param {string} sessionId
   * @returns {Promise} InterimSummaryDetailResponse, including citation_map
   */
  getLatestInterimSummary: (sessionId) => api.get(`/sessions/${sessionId}/summary/interim/latest`),

  /**
   * Get all summaries (interim + session) for a session
   * @param {string} sessionId
   * @returns {Promise} Array of InterimSummaryDetailResponse (summary_number=0 for final), each with citation_map
   */
  getAllSummaries: (sessionId) => api.get(`/sessions/${sessionId}/summary/all`),

  // ── Editable summaries (append-only writer edits) ──────────────────────

  /**
   * Head provenance + staleness for the editable-summary panel.
   * @returns {Promise} { has_summary, head_source, session_message_count,
   *   head_message_count, staleness_delta, refresh_due }
   */
  getSummaryStatus: (sessionId) => api.get(`/sessions/${sessionId}/summary/status`),

  /**
   * Save a writer's edit as a NEW summary version (append-only). Becomes head.
   * @returns {Promise} InterimSummaryDetailResponse (source='writer_edited')
   */
  writerEditSummary: (sessionId, content) =>
    api.post(`/sessions/${sessionId}/summary/writer-edit`, { content }),

  /**
   * Generate a transient merged draft (writer edit + new material). Not
   * persisted — review/correct, then save via writerEditSummary.
   * The body is `{ content }`. A content-only response is an unsaved draft:
   * the review must not reuse the saved row's number, date, count, or badge.
   * When a response also includes summary_number, message_count, created_at,
   * or source, the review panel uses those fields.
   * @returns {Promise} { content }
   */
  mergeDraftSummary: (sessionId) =>
    api.post(`/sessions/${sessionId}/summary/merge-draft`),

  // Session Folders: Reorder and sidebar endpoints

  /**
   * Reorder a session within its context (folder or root level)
   * @param {string} sessionId - Session UUID to reorder
   * @param {string|null} afterId - ID of item to place after
   * @param {string|null} beforeId - ID of item to place before
   * @returns {Promise<Object>} Updated session
   */
  reorder: (sessionId, afterId, beforeId) =>
    api.post(`/sessions/${sessionId}/reorder`, { after_id: afterId, before_id: beforeId }),

  /**
   * Get combined sidebar data for a story (folders + sessions in one call)
   * @param {string} storyId - Story UUID
   * @returns {Promise<Object>} { items: [...] } with interleaved folders and sessions
   */
  getStorySidebar: (storyId) => api.get(`/stories/${storyId}/sidebar`),

  /**
   * Get combined sidebar data for a project (folders + sessions in one call)
   * @param {string} projectId - Project UUID
   * @returns {Promise<Object>} { items: [...] } with interleaved folders and sessions
   */
  getProjectSidebar: (projectId) => api.get(`/projects/${projectId}/sidebar`),

  // Focus Session endpoints

  /**
   * Start over a logline focus session (clears messages and summaries, re-inserts welcome message)
   * @param {string} sessionId - Session UUID
   * @returns {Promise<Object>} Success response with new welcome message
   */
  startOver: (sessionId) => api.post(`/sessions/${sessionId}/start-over`),

  /**
   * Judge beat coverage in this Plot Focus conversation (session-local).
   * @returns {Promise<Object>} Updated session including plot_focus.gap_state
   */
  analyzePlotGaps: (sessionId) => api.post(`/sessions/${sessionId}/plot-focus/gaps`),

  reviewSceneStubs: (sessionId) =>
    api.post(`/sessions/${sessionId}/scene-stub-list/review`, {}),

  /**
   * Keep talking or open Plot Workshop (#985 / #995). Persists skip so Discovery
   * stays quiet until a new solid thread or much more depth.
   */
  plotReadyOfferChoice: (sessionId, status) =>
    api.post(`/sessions/${sessionId}/plot-ready-offer`, { status }),

  // Element Status endpoints (for SessionPicker)

  /**
   * Get element development status for a story
   * @param {string} storyId - Story UUID
   * @returns {Promise<Object>} ElementsStatusResponse with:
   *   - developed_elements: Dict mapping template type to list of elements with coverage
   *   - discovered_elements: List of elements found in explore sessions
   *   - logline_status: Logline development status including session_id, has_logline, logline text
   */
  getStoryElementsStatus: (storyId) => api.get(`/stories/${storyId}/elements-status`),
};
